import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor } from '../../contexts/EditorContext';

const Workspace = () => {
  const { state, actions, currentPage } = useEditor();
  const editorRef = useRef(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [elementActions, setElementActions] = useState(null);
  
  // Track if we should skip next content update (prevents loops)
  const skipNextContentUpdateRef = useRef(false);
  
  // FIXED: Immediate save function (no debounce)
  const saveContentImmediately = useCallback(() => {
    if (editorRef.current && !skipNextContentUpdateRef.current) {
      const content = editorRef.current.innerHTML;
      
      // Remove selection artifacts before saving
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Remove all selection classes and inline styles added by editor
      tempDiv.querySelectorAll('.element-selected').forEach(el => {
        el.classList.remove('element-selected');
      });
      
      // Remove element actions
      tempDiv.querySelectorAll('.element-actions').forEach(el => {
        el.remove();
      });
      
      const cleanContent = tempDiv.innerHTML;
      actions.updatePageContent(cleanContent);
    }
  }, [actions]);
  
  // Store cursor position
  const saveCursorPosition = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return null;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return null;
    
    const range = selection.getRangeAt(0);
    
    if (!editor.contains(range.startContainer) && !editor.isSameNode(range.startContainer)) {
      return null;
    }
    
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    
    return {
      startOffset: range.startOffset,
      startContainer: range.startContainer,
      endOffset: range.endOffset,
      endContainer: range.endContainer,
      position: preCaretRange.toString().length
    };
  }, []);

  const restoreCursorPosition = useCallback((cursorInfo) => {
    if (!cursorInfo || !editorRef.current) return;
    
    const editor = editorRef.current;
    const walker = document.createTreeWalker(
      editor,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let currentNode;
    let currentPosition = 0;
    
    while ((currentNode = walker.nextNode())) {
      const nodeLength = currentNode.textContent.length;
      
      if (currentPosition <= cursorInfo.position && 
          currentPosition + nodeLength >= cursorInfo.position) {
        const offset = Math.min(cursorInfo.position - currentPosition, nodeLength);
        const range = document.createRange();
        const selection = window.getSelection();
        
        range.setStart(currentNode, offset);
        range.setEnd(currentNode, offset);
        selection.removeAllRanges();
        selection.addRange(range);
        
        editor.focus();
        break;
      }
      currentPosition += nodeLength;
    }
  }, []);

  // FIXED: Handle image upload with immediate save
  const handleImageUpload = useCallback((element, file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target.result;
      
      if (element.classList.contains('image-upload-area')) {
        const preview = element.querySelector('.profile-preview');
        const placeholder = element.querySelector('.image-placeholder');
        
        if (preview) {
          preview.src = imageUrl;
          preview.style.display = 'block';
          preview.style.width = '100%';
          preview.style.height = '100%';
          preview.style.objectFit = 'cover';
        }
        if (placeholder) {
          placeholder.style.display = 'none';
        }
        
        element.classList.add('element-selected');
        setSelectedElement(element);
        
        const rect = element.getBoundingClientRect();
        const editor = editorRef.current;
        const editorRect = editor.getBoundingClientRect();
        const editorScroll = editor.scrollTop;
        
        setElementActions({
          top: rect.top - editorRect.top + editorScroll - 40,
          left: rect.left - editorRect.left + (rect.width / 2)
        });
        
        actions.selectElement({
          element: element,
          type: 'image-container',
          styles: window.getComputedStyle(element)
        });
      }
      
      // FIXED: Save immediately after image upload
      saveContentImmediately();
    };
    reader.readAsDataURL(file);
  }, [actions, saveContentImmediately]);

  const handleReplaceImage = useCallback(() => {
    if (!selectedElement) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        handleImageUpload(selectedElement, file);
      }
      document.body.removeChild(input);
    };
    
    document.body.appendChild(input);
    input.click();
  }, [selectedElement, handleImageUpload]);

  const handleResetImageContainer = useCallback(() => {
    if (!selectedElement || !selectedElement.classList.contains('image-upload-area')) return;
    
    const preview = selectedElement.querySelector('.profile-preview');
    const placeholder = selectedElement.querySelector('.image-placeholder');
    
    if (preview) {
      preview.src = '';
      preview.style.display = 'none';
    }
    if (placeholder) {
      placeholder.style.display = 'block';
    }
    
    selectedElement.classList.remove('element-selected');
    setSelectedElement(null);
    setElementActions(null);
    actions.selectElement(null);
    
    // FIXED: Save immediately after reset
    saveContentImmediately();
  }, [selectedElement, actions, saveContentImmediately]);

  // Apply document styles to editor container only
  useEffect(() => {
    if (editorRef.current && state.design) {
      const editor = editorRef.current;
      
      editor.style.fontFamily = state.design.fontFamily || 'Inter, system-ui';
      editor.style.backgroundColor = state.design.backgroundColor || '#ffffff';
      editor.style.color = state.design.textColor || '#2d3748';
      editor.style.fontSize = `${state.design.fontSize || 14}px`;
      editor.style.lineHeight = state.design.lineHeight || 1.6;
      editor.style.letterSpacing = state.design.letterSpacing || 'normal';
      
      const zoom = state.zoom / 100;
      editor.style.transform = `scale(${zoom})`;
      editor.style.transformOrigin = 'top center';
      editor.style.width = '620px';
    }
  }, [state.design, state.zoom]);

  // Handle element selection
  const handleElementClick = useCallback((e) => {
    const element = e.target;
    const editor = editorRef.current;
    
    if (element.closest('.image-upload-area')) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (element.closest('.element-actions') || element.closest('.element-action-btn')) {
      return;
    }
    
    if (element === editor) {
      setSelectedElement(null);
      setElementActions(null);
      actions.selectElement(null);
      
      document.querySelectorAll('.element-selected').forEach(el => {
        el.classList.remove('element-selected');
      });
      return;
    }
    
    const uploadArea = element.closest('.image-upload-area');
    if (uploadArea) {
      const preview = uploadArea.querySelector('.profile-preview');
      const hasImage = preview && preview.src && preview.style.display !== 'none';
      
      if (hasImage) {
        e.stopPropagation();
        
        document.querySelectorAll('.element-selected').forEach(el => {
          el.classList.remove('element-selected');
          const actions = el.querySelector('.element-actions');
          if (actions) actions.style.display = 'none';
        });
        
        uploadArea.classList.add('element-selected');
        setSelectedElement(uploadArea);
        
        const rect = uploadArea.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();
        const editorScroll = editor.scrollTop;
        
        setElementActions({
          top: rect.top - editorRect.top + editorScroll - 40,
          left: rect.left - editorRect.left + (rect.width / 2)
        });
        
        actions.selectElement({
          element: uploadArea,
          type: 'image-container',
          styles: window.getComputedStyle(uploadArea)
        });
        
        return;
      } else {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.style.display = 'none';
        
        input.onchange = (event) => {
          const file = event.target.files?.[0];
          if (file) {
            handleImageUpload(uploadArea, file);
          }
          document.body.removeChild(input);
        };
        
        document.body.appendChild(input);
        input.click();
        return;
      }
    }
    
    let selectableElement = element;
    while (selectableElement && selectableElement !== editor) {
      if (selectableElement.tagName && 
          ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SPAN', 'DIV', 'LI', 'TD', 'IMG', 'UL', 'OL', 'TABLE', 'TR'].includes(selectableElement.tagName)) {
        break;
      }
      selectableElement = selectableElement.parentNode;
    }
    
    if (!selectableElement || selectableElement === editor) return;
    
    e.stopPropagation();
    
    if (selectedElement === selectableElement) return;
    
    document.querySelectorAll('.element-selected').forEach(el => {
      el.classList.remove('element-selected');
      const actions = el.querySelector('.element-actions');
      if (actions) actions.style.display = 'none';
    });
    
    selectableElement.classList.add('element-selected');
    setSelectedElement(selectableElement);
    
    const rect = selectableElement.getBoundingClientRect();
    const editorRect = editor.getBoundingClientRect();
    const editorScroll = editor.scrollTop;
    
    setElementActions({
      top: rect.top - editorRect.top + editorScroll - 40,
      left: rect.left - editorRect.left + (rect.width / 2)
    });
    
    const inlineActions = selectableElement.querySelector('.element-actions');
    if (inlineActions) {
      inlineActions.style.display = 'flex';
    }
    
    actions.selectElement({
      element: selectableElement,
      type: selectableElement.tagName.toLowerCase(),
      styles: window.getComputedStyle(selectableElement)
    });
  }, [actions, selectedElement, handleImageUpload]);

  const handleDeleteElement = useCallback(() => {
    if (selectedElement && selectedElement.parentNode) {
      if (selectedElement.classList.contains('image-upload-area')) {
        handleResetImageContainer();
      } else {
        const cursorInfo = saveCursorPosition();
        selectedElement.remove();
        
        // FIXED: Save immediately after deletion
        saveContentImmediately();
        
        restoreCursorPosition(cursorInfo);
        setSelectedElement(null);
        setElementActions(null);
        actions.selectElement(null);
      }
    }
  }, [selectedElement, saveCursorPosition, restoreCursorPosition, actions, handleResetImageContainer, saveContentImmediately]);

  const handleDuplicateElement = useCallback(() => {
    if (selectedElement && selectedElement.parentNode) {
      const cursorInfo = saveCursorPosition();
      const clone = selectedElement.cloneNode(true);
      
      clone.classList.remove('element-selected');
      
      if (clone.classList.contains('image-upload-area')) {
        const preview = clone.querySelector('.profile-preview');
        const placeholder = clone.querySelector('.image-placeholder');
        
        if (preview) {
          preview.src = '';
          preview.style.display = 'none';
        }
        if (placeholder) {
          placeholder.style.display = 'block';
        }
      }
      
      selectedElement.parentNode.insertBefore(clone, selectedElement.nextSibling);
      
      // FIXED: Save immediately after duplication
      saveContentImmediately();
      
      restoreCursorPosition(cursorInfo);
    }
  }, [selectedElement, saveCursorPosition, restoreCursorPosition, saveContentImmediately]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedElement) {
        e.preventDefault();
        handleDeleteElement();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElement) {
        e.preventDefault();
        handleDuplicateElement();
      }
      if (e.key === 'Escape') {
        setSelectedElement(null);
        setElementActions(null);
        actions.selectElement(null);
        
        document.querySelectorAll('.element-selected').forEach(el => {
          el.classList.remove('element-selected');
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, handleDeleteElement, handleDuplicateElement, actions]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const editor = editorRef.current;
      const rightSidebar = document.querySelector('.right-sidebar');
      
      if (rightSidebar && rightSidebar.contains(e.target)) {
        return;
      }
      
      if (editor && !editor.contains(e.target) && 
          !e.target.closest('.element-actions') && 
          !e.target.closest('.element-action-btn')) {
        setSelectedElement(null);
        setElementActions(null);
        actions.selectElement(null);
        
        document.querySelectorAll('.element-selected').forEach(el => {
          el.classList.remove('element-selected');
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actions]);

  // Load page content
  useEffect(() => {
    if (editorRef.current && currentPage) {
      const currentContent = editorRef.current.innerHTML;
      const pageContent = currentPage.content || '';
      
      if (currentContent !== pageContent) {
        skipNextContentUpdateRef.current = true;
        
        const cursorInfo = saveCursorPosition();
        
        editorRef.current.innerHTML = pageContent;
        
        document.querySelectorAll('.element-selected').forEach(el => {
          el.classList.remove('element-selected');
        });
        
        setupImageUploadAreas();
        
        if (cursorInfo) {
          setTimeout(() => restoreCursorPosition(cursorInfo), 50);
        }
        
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.focus();
          }
          setTimeout(() => {
            skipNextContentUpdateRef.current = false;
          }, 100);
        }, 100);
      }
    }
  }, [currentPage, saveCursorPosition, restoreCursorPosition]);

  const setupImageUploadAreas = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const uploadAreas = editor.querySelectorAll('.image-upload-area');
    uploadAreas.forEach(area => {
      let preview = area.querySelector('.profile-preview');
      let placeholder = area.querySelector('.image-placeholder');
      
      if (!preview) {
        preview = document.createElement('img');
        preview.className = 'profile-preview';
        preview.style.display = 'none';
        preview.style.width = '100%';
        preview.style.height = '100%';
        preview.style.objectFit = 'cover';
        preview.style.position = 'absolute';
        preview.style.top = '0';
        preview.style.left = '0';
        area.appendChild(preview);
      }
      
      if (!placeholder) {
        placeholder = document.createElement('span');
        placeholder.className = 'image-placeholder';
        placeholder.style.color = '#999';
        placeholder.style.fontSize = '14px';
        placeholder.style.textAlign = 'center';
        placeholder.innerHTML = 'Click to upload<br/>Profile Image';
        area.appendChild(placeholder);
      }
      
      area.style.cursor = 'pointer';
    });
  }, []);

  // FIXED: Event listeners with immediate save on input
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInput = () => {
      // FIXED: Save immediately on input
      saveContentImmediately();
    };

    const handlePaste = (e) => {
      e.preventDefault();
      
      const items = (e.clipboardData || e.originalEvent.clipboardData).items;
      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          const reader = new FileReader();
          reader.onload = (event) => {
            const img = document.createElement('img');
            img.src = event.target.result;
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.margin = '10px 0';
            
            const selection = window.getSelection();
            if (selection.rangeCount) {
              const range = selection.getRangeAt(0);
              range.deleteContents();
              range.insertNode(img);
              
              // FIXED: Save immediately after paste
              saveContentImmediately();
            }
          };
          reader.readAsDataURL(file);
          return;
        }
      }
      
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
      
      // FIXED: Save immediately after paste
      saveContentImmediately();
    };

    editor.addEventListener('input', handleInput);
    editor.addEventListener('paste', handlePaste);
    editor.addEventListener('click', handleElementClick);

    setupImageUploadAreas();

    return () => {
      editor.removeEventListener('input', handleInput);
      editor.removeEventListener('paste', handlePaste);
      editor.removeEventListener('click', handleElementClick);
    };
  }, [handleElementClick, setupImageUploadAreas, saveContentImmediately]);

  // Update element actions position on scroll
  useEffect(() => {
    const updateElementActionsPosition = () => {
      if (selectedElement && editorRef.current) {
        const rect = selectedElement.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        const editorScroll = editorRef.current.scrollTop;
        
        setElementActions({
          top: rect.top - editorRect.top + editorScroll - 40,
          left: rect.left - editorRect.left + (rect.width / 2)
        });
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('scroll', updateElementActionsPosition);
      window.addEventListener('resize', updateElementActionsPosition);
      
      return () => {
        editor.removeEventListener('scroll', updateElementActionsPosition);
        window.removeEventListener('resize', updateElementActionsPosition);
      };
    }
  }, [selectedElement]);

  // Drag and drop
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const uploadArea = e.target.closest('.image-upload-area');
      if (uploadArea) {
        uploadArea.style.borderColor = '#1ca29b';
        uploadArea.style.backgroundColor = 'rgba(28, 162, 155, 0.1)';
      } else {
        editor.style.backgroundColor = 'rgba(28, 162, 155, 0.05)';
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const uploadAreas = editor.querySelectorAll('.image-upload-area');
      uploadAreas.forEach(area => {
        area.style.borderColor = '#b8a46f';
        area.style.backgroundColor = 'transparent';
      });
      
      editor.style.backgroundColor = state.design.backgroundColor || '#ffffff';
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const uploadAreas = editor.querySelectorAll('.image-upload-area');
      uploadAreas.forEach(area => {
        area.style.borderColor = '#b8a46f';
        area.style.backgroundColor = 'transparent';
      });
      editor.style.backgroundColor = state.design.backgroundColor || '#ffffff';
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          const uploadArea = e.target.closest('.image-upload-area');
          if (uploadArea) {
            handleImageUpload(uploadArea, file);
          } else {
            const reader = new FileReader();
            reader.onload = (event) => {
              const img = document.createElement('img');
              img.src = event.target.result;
              img.style.maxWidth = '100%';
              img.style.height = 'auto';
              img.style.margin = '10px 0';
              
              const x = e.clientX;
              const y = e.clientY;
              
              const range = document.caretRangeFromPoint(x, y);
              if (range) {
                range.insertNode(img);
              } else {
                editor.appendChild(img);
              }
              
              // FIXED: Save immediately after drop
              saveContentImmediately();
            };
            reader.readAsDataURL(file);
          }
        }
      }
    };

    editor.addEventListener('dragover', handleDragOver);
    editor.addEventListener('dragleave', handleDragLeave);
    editor.addEventListener('drop', handleDrop);

    return () => {
      editor.removeEventListener('dragover', handleDragOver);
      editor.removeEventListener('dragleave', handleDragLeave);
      editor.removeEventListener('drop', handleDrop);
    };
  }, [state.design.backgroundColor, handleImageUpload, saveContentImmediately]);

  // Mutation observer
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          setupImageUploadAreas();
        }
      });
    });

    observer.observe(editor, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [setupImageUploadAreas]);

  // Sync selected element with context
  useEffect(() => {
    if (state.selectedElement && !selectedElement) {
      const element = state.selectedElement.element;
      if (element && editorRef.current && editorRef.current.contains(element)) {
        setSelectedElement(element);
        
        const rect = element.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        const editorScroll = editorRef.current.scrollTop;
        
        setElementActions({
          top: rect.top - editorRect.top + editorScroll - 40,
          left: rect.left - editorRect.left + (rect.width / 2)
        });
      }
    } else if (!state.selectedElement && selectedElement) {
      setSelectedElement(null);
      setElementActions(null);
    }
  }, [state.selectedElement, selectedElement]);

  return (
    <div className="workspace" id="workspace">
      <div className="canvas-wrap">
        <div 
          className="page" 
          id="page"
          style={{
            backgroundColor: '#ffffff'
          }}
        >
          <div 
            ref={editorRef}
            id="editor"
            contentEditable="true"
            spellCheck="false"
            suppressContentEditableWarning
            style={{
              width: '620px',
              minHeight: '800px',
              background: 'transparent',
              margin: '0 auto',
              outline: 'none',
              cursor: 'text',
            }}
          />
          
          {selectedElement && elementActions && (
            <div 
              className="element-actions"
              style={{
                position: 'absolute',
                top: `${elementActions.top}px`,
                left: `${elementActions.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 1000
              }}
            >
              <button 
                className="element-action-btn duplicate"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDuplicateElement();
                }}
                title="Duplicate (Ctrl+D)"
              >
                <i className="fas fa-copy"></i>
              </button>
              
              {selectedElement.classList.contains('image-upload-area') && 
               selectedElement.querySelector('.profile-preview')?.style?.display === 'block' ? (
                <>
                  <button 
                    className="element-action-btn replace"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleReplaceImage();
                    }}
                    title="Replace Image"
                  >
                    <i className="fas fa-exchange-alt"></i>
                  </button>
                  <button 
                    className="element-action-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeleteElement();
                    }}
                    title="Delete"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </>
              ) : (
                <button 
                  className="element-action-btn delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteElement();
                  }}
                  title="Delete (Del)"
                >
                  <i className="fas fa-trash"></i>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Workspace;